/*! JointJS+ v4.1.0 - HTML5 Diagramming Framework

Copyright (c) 2024 client IO

 2024-12-04


This Source Code Form is subject to the terms of the JointJS+ License
, v. 2.0. If a copy of the JointJS+ License was not distributed with this
file, You can obtain one at https://www.jointjs.com/license
 or from the JointJS+ archive as was distributed by client IO. See the LICENSE file.*/

import { Appconfig } from "src/app/configurations/appconfig";
import { tools } from "../utils/shapeControls";
import { textAttrs, textMarkup, cardTextAttrs, headerRectangleMarkup, headerRectangleAttrs, supplierColor, getSupplierColorKey, otColor, getOTColorKey } from "../config/markups";
// OT assessment dimension keys (structural property names - these don't change)
const OT_ASSESSMENT_DIMENSION_KEYS = [
    'fitForPurpose',
    'costEfficiency',
    'supportability',
    'vendorRisk',
    'securityCompliance',
    'resilience',
    'businessAlignment',
    'utilisation',
    'technologyObsolescence',
    'operationalDependency',
    'dataIntegration'
];

export const dynamicGroups: any = [
    {
        id: 'projects',
        label: 'Projects',
        nameKey: 'prgName',
        colorKey: 'rag',
        func: 'getProjectData',
        colors: {
            amber: {
                fill: '#ff8c00',
                text: '#FFFFFF'
            },
            green: {
                fill: '#228b22',
                text: '#FFFFFF'
            },
            red: {
                fill: '#ff0000',
                text: '#FFFFFF'
            },
            grey: {
                fill: '#808080',
                text: '#FFFFFF'
            },
            default: {
                fill: '#808080',
                text: '#FFFFFF'
            }
        }
    },
    {
        id: 'applications',
        label: 'Applications',
        nameKey: 'appName',
        func: 'getApplicationDataForProject',
        colorKey: 'taQusAvg',
        getColor: (val: any) => {
            let fill: any;
            let text: any = '#FFFFFF';
            if (val > 4) {
                fill = Appconfig.CriticalColorIndicators['green'];
            }
            else if (val > 2) {
                fill = Appconfig.CriticalColorIndicators['amber'];
            }
            else if (val > 0) {
                fill = Appconfig.CriticalColorIndicators['red'];
            }
            else if (val !== undefined) {
                fill = Appconfig.CriticalColorIndicators['grey'];
                text = PRIMARY_SHAPE_COLOR;
            }
            else {
                fill = Appconfig.CriticalColorIndicators['grey'];
                text = PRIMARY_SHAPE_COLOR;
            }
            return { fill, text: text };
        },
        colors: {
            default: {
                fill: '#FB54A9',
                text: '#FFFFFF'
            }
        }
    },
    {
        id: 'suppliers',
        label: 'Suppliers',
        nameKey: 'supplierName',
        func: 'getSupplierDataForProject',
        colors: {
            default: {
                fill: '#F27F31',
                text: '#FFFFFF'
            }
        },
        getColor: (item: any) => {
            const fields = [
                "competency",
                "innovation",
                "performance",
                "quality",
                "delivery_efficiency",
                "proactive",
                "mindset",
                "risk",
                "cost",
                "perception",
                "talent"
            ];
            let worstColor = 'bright-secondary';
            let worstValue = 0;
            for (const field of fields) {
                const colorKey = getSupplierColorKey(item[field]);
                const value = supplierColor[colorKey]?.order
                if (value && value > worstValue) {
                    worstValue = value;
                    worstColor = colorKey;
                }
            }
            return supplierColor[worstColor];
        }
    },
    {
        id: 'otdata',
        label: 'Operational Technologies',
        nameKey: 'assetName',
        func: 'getOTData',
        colors: {
            default: {
                fill: '#009688',
                text: '#FFFFFF'
            }
        },
        getColor: (item: any) => {
            const fields = OT_ASSESSMENT_DIMENSION_KEYS;
            let worstColor = 'grey';
            let worstValue = -1;
            let hasAssessed = false;
            
            for (const field of fields) {
                const concernValue = item[field];
                if (concernValue && concernValue !== 'Not Assessed' && concernValue !== 'Not Applicable') {
                    hasAssessed = true;
                    const colorKey = getOTColorKey(concernValue);
                    const value = otColor[colorKey]?.order;
                    if (value !== undefined && value > worstValue) {
                        worstValue = value;
                        worstColor = colorKey;
                    }
                }
            }
            
            // If all are not assessed, return grey
            if (!hasAssessed) {
                return otColor['grey'];
            }
            
            // Return worst severity color
            return otColor[worstColor];
        }
    },
    {
        id: 'resources',
        label: 'People',
        nameKey: 'empName',
        func: 'getHRDataForProject',
        colors: {
            default: {
                fill: '#3771B8',
                text: '#FFFFFF'
            }
        }
    }
];

export const stencilGroups = {
    basic: { index: 1, label: 'Basic shapes' },
    advanced: { index: 2, label: 'Advanced shapes', height: 100 },
    ...(dynamicGroups.reduce((acc: any, group: any, index: number) => {
        acc[group.id] = {
            index: index + 2,
            label: group.label,
            closed: true,
            layout: {
                columns: 1,
                marginX: 10,
                marginY: 16,
                rowGap: 10,
                rowHeight: 32,
                columnWidth: 200
            }
        }
        return acc
    }, {}))
};

const PRIMARY_SHAPE_COLOR = '#353535';
const PRIMARY_SHAPE_FILL = '#ffffff';



export const stencilShapes = {
    basic: [
        {
            type: 'standard.Rectangle',
            name: 'Square',
            markup: textMarkup(),
            attrs: {
                body: {
                    rx: 0,
                    ry: 0,
                    fill: PRIMARY_SHAPE_FILL,
                    stroke: PRIMARY_SHAPE_COLOR,
                    strokeWidth: 2,
                    strokeDasharray: '0'
                },
                ...textAttrs({ padding: '10px' }),
                control: tools.square.control
            },
            targetAttributes: {
                size: { width: 64, height: 64 }
            }
        },
        {
            type: 'standard.Ellipse',
            name: 'Circle',
            markup: textMarkup('', { tagName: 'ellipse', selector: 'body' }),
            attrs: {
                body: {
                    fill: PRIMARY_SHAPE_FILL,
                    stroke: PRIMARY_SHAPE_COLOR,
                    strokeWidth: 2,
                    strokeDasharray: '0'
                },
                ...textAttrs({ padding: '10px' })
            },
            targetAttributes: {
                size: { width: 64, height: 64 }
            }
        },
        {
            type: 'standard.Polygon',
            name: 'Rhombus',
            markup: textMarkup('', { tagName: 'polygon', selector: 'body' }),
            attrs: {
                body: {
                    points: 'calc(0.5 * w),0 calc(w),calc(0.5 * h) calc(0.5 * w),calc(h) 0,calc(0.5 * h)',
                    fill: PRIMARY_SHAPE_FILL,
                    stroke: PRIMARY_SHAPE_COLOR,
                    strokeWidth: 2,
                    strokeDasharray: '0'
                },
                ...textAttrs({ padding: '10px' })
            },
            targetAttributes: {
                size: { width: 64, height: 64 }
            }
        },
        {
            // Triangle pointing up
            type: 'standard.Path',
            size: { width: 60, height: 40 },
            name: 'Triangle',
            markup: textMarkup('', { tagName: 'path', selector: 'body' }),
            attrs: {
                body: {
                    d: 'M calc(0.5*w) 0 calc(w) calc(h) H 0 Z',
                    stroke: PRIMARY_SHAPE_COLOR,
                    strokeDasharray: '0'
                },
                ...textAttrs({ padding: '10px' })
            },
            targetAttributes: {
                size: { width: 60, height: 40 }
            }
        },
        {
            // Hexagon
            type: 'standard.Path',
            size: { width: 60, height: 40 },
            name: 'Hexagon',
            markup: textMarkup('', { tagName: 'path', selector: 'body' }),
            attrs: {
                body: {
                    d: `
                        M 0 calc(0.5 * h)
                        L ${tools.hexagon.control.offset} calc(h)
                        L calc(w-${tools.hexagon.control.offset}) calc(h)
                        L calc(w) calc(0.5 * h)
                        L calc(w-${tools.hexagon.control.offset}) 0
                        L ${tools.hexagon.control.offset} 0
                        Z
                    `,
                    stroke: PRIMARY_SHAPE_COLOR,
                    strokeDasharray: '0'
                },
                ...textAttrs({ padding: '10px' }),
                control: tools.hexagon.control
            },
            targetAttributes: {
                size: { width: 60, height: 40 }
            }
        },
        {
            // Parallelogram
            type: 'standard.Path',
            size: { width: 60, height: 40 },
            name: 'Parallelogram',
            markup: textMarkup('', { tagName: 'path', selector: 'body' }),
            attrs: {
                body: {
                    d: `
                        M 0 calc(h)
                        L ${tools.parallelogram.control.offset} 0
                        L calc(w) 0
                        L calc(w-${tools.parallelogram.control.offset}) calc(h)
                        Z
                    `,
                    stroke: PRIMARY_SHAPE_COLOR,
                    strokeDasharray: '0'
                },
                ...textAttrs({ padding: '10px' }),
                control: tools.parallelogram.control
            },
            targetAttributes: {
                size: { width: 50, height: 40 }
            }
        },
        {
            // Trapezoid
            type: 'standard.Path',
            size: { width: 60, height: 40 },
            name: 'Trapezoid',
            markup: textMarkup('', { tagName: 'path', selector: 'body' }),
            attrs: {
                body: {
                    d: `
                        M 0 calc(h)
                        L calc(w) calc(h)
                        L calc(w - ${tools.trapezoid.control.offset}) 0
                        L ${tools.trapezoid.control.offset} 0
                        Z
                    `,
                    stroke: PRIMARY_SHAPE_COLOR,
                    strokeDasharray: '0'
                },
                ...textAttrs({ padding: '10px' }),
                control: tools.trapezoid.control
            },
            targetAttributes: {
                size: { width: 60, height: 40 }
            }
        },
        {
            // Arrow
            type: 'standard.Path',
            size: { width: 60, height: 40 },
            name: 'Arrow',
            markup: textMarkup('', { tagName: 'path', selector: 'body' }),
            attrs: {
                body: {
                    d: `
                        M calc(w - ${tools.arrow.control.arrowHeight}) 0
                        L calc(w) calc(0.5*h)
                        L calc(w - ${tools.arrow.control.arrowHeight})  calc(h)
                        v -calc(h/2 - ${tools.arrow.control.thickness / 2})
                        H 0
                        v -${tools.arrow.control.thickness}
                        H calc(w - ${tools.arrow.control.arrowHeight})
                        z
                        `,
                    stroke: PRIMARY_SHAPE_COLOR,
                    strokeDasharray: '0'
                },
                ...textAttrs({ padding: '10px' }),
                control: tools.arrow.control
            },
            targetAttributes: {
                size: { width: 80, height: 50 }
            }
        },
        {
            // Step
            type: 'standard.Path',
            size: { width: 60, height: 40 },
            markup: textMarkup('', { tagName: 'path', selector: 'body' }),
            name: 'Step',
            attrs: {
                body: {
                    d: `
                        M 0 0
                        L ${tools.step.control.offset} calc(0.5 * h)
                        L 0 calc(h)
                        L calc(w - ${tools.step.control.offset}) calc(h)
                        L calc(w) calc(0.5 * h)
                        L calc(w - ${tools.step.control.offset}) 0
                        Z
                    `,
                    stroke: PRIMARY_SHAPE_COLOR,
                    strokeDasharray: '0'
                },
                ...textAttrs({ padding: '10px' }),
                control: tools.step.control
            },
            targetAttributes: {
                size: { width: 60, height: 40 }
            }
        },
        {
            // In Arrow
            type: 'standard.Path',
            size: { width: 60, height: 40 },
            name: 'In Arrow',
            markup: textMarkup('', { tagName: 'path', selector: 'body' }),
            attrs: {
                body: {
                    d: `
                    M 0 0
                    L calc(0.85*w) 0
                    L calc(w) calc(0.5*h)
                    L calc(0.85*w) calc(h)
                    L 0 calc(h)
                    Z
                    `,
                    stroke: PRIMARY_SHAPE_COLOR,
                    strokeDasharray: '0'
                },
                ...textAttrs({ padding: '10px' })
            },
            targetAttributes: {
                size: { width: 60, height: 40 }
            }
        },
        {
            type: 'standard.Cylinder',
            size: { width: 64, height: 50 },
            markup: textMarkup('', [{ tagName: 'path', selector: 'body' }, { tagName: 'ellipse', selector: 'top' }]),
            name: 'Cylinder',
            attrs: {
                body: {
                    lateralArea: 5,
                    fill: PRIMARY_SHAPE_FILL,
                    stroke: PRIMARY_SHAPE_COLOR,
                    strokeWidth: 2,
                    strokeDasharray: '0'
                },
                top: {
                    cy: 5,
                    ry: 5,
                    fill: PRIMARY_SHAPE_FILL,
                    stroke: PRIMARY_SHAPE_COLOR,
                    strokeWidth: 2,
                    strokeDasharray: '0'
                },
                ...textAttrs({ padding: '10px' })
            },
            targetAttributes: {
                size: { width: 64, height: 64 }
            }
        },
        {
            type: 'standard.TextBlock',
            size: { width: 64, height: 50 },
            name: 'Text',
            attrs: {
                body: {
                    fill: 'transparent',
                    stroke: 'transparent',
                },
                ...textAttrs()
            },
            markup: textMarkup('Text'),
            targetAttributes: {
                size: { width: 50, height: 20 }
            }
        }
    ],
    advanced: [
        {
            type: 'standard.Image',
            size: { width: 53, height: 42 },
            name: '',
            attrs: {
                image: {
                    xlinkHref: 'assets/jointjs/image-icon1.svg'
                },
                body: {
                    fill: PRIMARY_SHAPE_FILL,
                    stroke: PRIMARY_SHAPE_COLOR,
                    strokeWidth: 2,
                    strokeDasharray: '0'
                },
                label: {
                    fill: PRIMARY_SHAPE_COLOR,
                    fontFamily: 'Open Sans',
                    fontWeight: 'Normal',
                    fontSize: 11
                }
            },
            targetAttributes: {
                size: { width: 53, height: 42 },
                name: 'Image'
            }
        },
        {
            type: 'standard.EmbeddedImage',
            size: { width: 90, height: 54 },
            name: 'Card',
            markup: textMarkup('', [{ tagName: 'rect', selector: 'body' }, { tagName: 'image', selector: 'image' }]),
            attrs: {
                body: {
                    stroke: PRIMARY_SHAPE_COLOR,
                    strokeWidth: 2,
                    strokeDasharray: '0'
                },
                image: {
                    xlinkHref: 'assets/jointjs/image-icon1.svg',
                    width: 'calc(0.4 * w)',
                    height: 'calc(h)',
                    x: 3,
                    y: 0,
                },
                ...cardTextAttrs()
            },
            targetAttributes: {
                size: { width: 90, height: 54 },
                attrs: {
                    image: { width: 'calc(0.3*w)', height: 'calc(h-20)', x: 10, y: 10 }
                }
            }
        },
        {
            type: 'standard.HeaderedRectangle',
            size: { width: 90, height: 54 },
            name: 'Rectangle with header',
            markup: headerRectangleMarkup(),
            attrs: {
                body: {
                    fill: PRIMARY_SHAPE_FILL,
                    stroke: PRIMARY_SHAPE_COLOR,
                    strokeWidth: 2,
                    strokeDasharray: '0'
                },
                header: {
                    stroke: PRIMARY_SHAPE_COLOR,
                    fill: PRIMARY_SHAPE_FILL,
                    strokeWidth: 2,
                    strokeDasharray: '0',
                    height: 10,
                },
                ...headerRectangleAttrs()
            },
            targetAttributes: {
                size: { width: 90, height: 90 },
                attrs: {
                    header: { height: 25 }
                }
            }
        },
        {
            type: 'app.Line',
            name: 'Line',
            size: { width: 60, height: 40 }
        },
        {
            type: 'app.Link',
            name: '',
            size: { width: 60, height: 40 },
            source: { x: 16, y: 90 },
            target: { x: 36, y: 75 },
            attrs: {
                line: {
                    stroke: PRIMARY_SHAPE_COLOR,
                    strokeWidth: 2,
                    targetMarker: {
                        type: 'path',
                        d: 'M 0 -5 -10 0 0 5 z'
                    }
                }
            },
            targetAttributes: {
                size: { width: 80, height: 40 },
                source: { x: 10, y: 50 },
                target: { x: 200, y: 50 },
            }
        }
    ]
};

function getColor(item: Record<string, any>, group: Record<string, any>) {
    let color: any = {}
    if (group["id"] === 'applications') {
        color = group["getColor"](item[group["colorKey"]])
    } else if (group["id"] === 'suppliers') {
        color = group["getColor"](item)
    } else if (group["id"] === 'otdata') {
        color = group["getColor"](item)
    } else {
        color = group["colors"][item[group["colorKey"]]] || group["colors"].default;
    }
    return color
}
export function getAppRectangle(item: Record<string, any>, group: Record<string, any>): Record<string, any> {
    const color = getColor(item, group)
    return {
        type: 'app.Rectangle',
        size: { width: 200, height: 32 },
        name: item[group["nameKey"]],
        markup: textMarkup(item[group["nameKey"]]),
        attrs: {
            body: {
                fill: color.fill,
                stroke: 'transparent',
            },
            textContainer: {
                style: {
                    color: color.text || '#353535'
                },
            },
            richText: {
                content: item[group["nameKey"]],
                style: {
                    textAlign: 'center'
                }
            },
            meta: {
                type: group["id"],
                id: item["_id"]
            }
        },
        targetAttributes: {
            size: { width: 200, height: 32 }
        }
    }
}

export function setAppRectangleChanges(cell: any, item: Record<string, any>, group: Record<string, any>) {
    const color = getColor(item, group)
    if (color) {
        cell.attrs.body.fill = color.fill
        if (cell.attrs.textContainer) {
            cell.attrs.textContainer.style.color = color.text || '#353535'
        }
        if (cell.attrs.richText) {
            cell.attrs.richText.content = item[group["nameKey"]]
        }
    }
}
